import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, X, Palette } from 'lucide-react';
import SpeakButton from './SpeakButton';
import DictateButton from './DictateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  { id: 'yellow', bg: '#FFF3A3', border: '#E6C95C', dot: '#FFC93C' },
  { id: 'pink',   bg: '#FFD6E7', border: '#E59BBA', dot: '#FF6FB5' },
  { id: 'blue',   bg: '#CDE8FF', border: '#7FB6E6', dot: '#4DA3E0' },
  { id: 'green',  bg: '#D9F7D0', border: '#85CC6E', dot: '#53B83A' },
  { id: 'purple', bg: '#E8D6FF', border: '#B897E0', dot: '#9671D1' },
  { id: 'orange', bg: '#FFD8B8', border: '#E8A96B', dot: '#F28B3E' },
];

const colorCfg = (id) => COLORS.find((c) => c.id === id) || COLORS[0];

// Slight rotation per sticky for the paper-pile feel
function rot(id) {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const deg = ((h % 7) - 3); // -3..+3 deg
  return `rotate(${deg}deg)`;
}

function StickyCard({ sticky, onChange, onDelete, onDragStart, onDragOver, onDragEnd, isDragging, isOver }) {
  const [content, setContent] = useState(sticky.content || '');
  const [showPalette, setShowPalette] = useState(false);
  const saveTimer = useRef(null);
  const cfg = colorCfg(sticky.color);

  useEffect(() => { setContent(sticky.content || ''); }, [sticky.content]);

  const scheduleSave = (newContent, newColor) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onChange(sticky.id, { content: newContent, color: newColor });
    }, 450);
  };

  const handleContent = (e) => {
    setContent(e.target.value);
    scheduleSave(e.target.value, sticky.color);
  };

  const setColor = (id) => {
    setShowPalette(false);
    onChange(sticky.id, { content, color: id });
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, sticky.id)}
      onDragOver={(e) => onDragOver(e, sticky.id)}
      onDragEnd={onDragEnd}
      className={`relative flex-shrink-0 w-56 h-40 p-3 border-2 border-black rounded-md shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40 scale-95' : 'hover:-translate-y-1'} ${isOver ? 'ring-4 ring-pink-400' : ''}`}
      style={{
        backgroundColor: cfg.bg,
        backgroundImage: `linear-gradient(135deg, transparent 94%, rgba(0,0,0,0.08) 96%)`,
        transform: isDragging ? 'rotate(-6deg)' : rot(sticky.id),
      }}
      data-testid={`sticky-${sticky.id}`}
    >
      {/* Top strip */}
      <div className="absolute top-1 left-1 right-1 flex items-center justify-between z-10">
        <button
          onClick={() => setShowPalette((v) => !v)}
          onMouseDown={(e) => e.stopPropagation()}
          className="w-5 h-5 rounded-full border-2 border-black"
          style={{ backgroundColor: cfg.dot }}
          title="Change color"
          data-testid={`sticky-color-${sticky.id}`}
        />
        <button
          onClick={() => onDelete(sticky.id)}
          className="w-5 h-5 rounded-full border-2 border-black bg-white hover:bg-red-400 flex items-center justify-center"
          title="Delete"
          data-testid={`sticky-delete-${sticky.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {showPalette && (
        <div className="absolute top-8 left-1 z-20 flex gap-1 p-1 bg-white border-2 border-black rounded-lg shadow">
          {COLORS.map((c) => (
            <button
              key={c.id}
              onClick={() => setColor(c.id)}
              className={`w-5 h-5 rounded-full border-2 border-black ${sticky.color === c.id ? 'ring-2 ring-pink-400' : ''}`}
              style={{ backgroundColor: c.dot }}
              title={c.id}
            />
          ))}
        </div>
      )}

      <textarea
        value={content}
        onChange={handleContent}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.stopPropagation()}
        maxLength={300}
        placeholder="Quick note…"
        className="w-full h-full mt-4 bg-transparent resize-none outline-none text-sm font-semibold font-['Nunito'] leading-snug placeholder-black/40 cursor-text pb-6"
        data-testid={`sticky-text-${sticky.id}`}
      />
      {content?.trim() && (
        <div className="absolute bottom-1 right-1 flex gap-1" onMouseDown={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()}>
          <DictateButton
            onAppend={(said) => {
              const newText = (content ? content + ' ' : '') + said;
              setContent(newText);
              scheduleSave(newText, sticky.color);
            }}
            size="xs"
          />
          <SpeakButton text={content} label="" size="xs" />
        </div>
      )}
      {!content?.trim() && (
        <div className="absolute bottom-1 right-1" onMouseDown={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()}>
          <DictateButton
            onAppend={(said) => {
              setContent(said);
              scheduleSave(said, sticky.color);
            }}
            size="xs"
          />
        </div>
      )}
    </div>
  );
}

export default function StickyRow() {
  const [stickies, setStickies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stickies`, { withCredentials: true });
      setStickies(data.items || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addSticky = async () => {
    const nextColor = COLORS[stickies.length % COLORS.length].id;
    try {
      const { data } = await axios.post(
        `${API}/stickies`,
        { content: '', color: nextColor },
        { withCredentials: true }
      );
      setStickies((s) => [...s, data.item]);
    } catch {
      // ignore
    }
  };

  const updateSticky = async (id, patch) => {
    setStickies((arr) => arr.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    try {
      await axios.patch(`${API}/stickies/${id}`, patch, { withCredentials: true });
    } catch {
      fetchAll();
    }
  };

  const deleteSticky = async (id) => {
    setStickies((arr) => arr.filter((s) => s.id !== id));
    try {
      await axios.delete(`${API}/stickies/${id}`, { withCredentials: true });
    } catch {
      fetchAll();
    }
  };

  const handleDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* ignore */ }
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggingId && draggingId !== id && overId !== id) {
      setOverId(id);
      // Live reorder: move dragging item before hovered one
      setStickies((arr) => {
        const from = arr.findIndex((s) => s.id === draggingId);
        const to = arr.findIndex((s) => s.id === id);
        if (from === -1 || to === -1 || from === to) return arr;
        const next = arr.slice();
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    }
  };

  const handleDragEnd = async () => {
    const wasDragging = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (!wasDragging) return;
    // Persist new order
    try {
      const order = stickies.map((s) => s.id);
      await axios.post(`${API}/stickies/reorder`, { order }, { withCredentials: true });
    } catch {
      fetchAll();
    }
  };

  if (loading) return null;

  return (
    <div className="mb-4" data-testid="sticky-row">
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-xs font-bold uppercase tracking-wider opacity-70 flex items-center gap-1">
          <Palette className="w-3.5 h-3.5" /> Post-its <span className="opacity-50 font-normal normal-case">· dra för att ordna om</span>
        </p>
        <span className="text-[10px] opacity-60">{stickies.length} / 50</span>
      </div>
      <div className="flex gap-5 overflow-x-auto pb-4 pt-2 px-1 -mx-1 snap-x">
        {stickies.map((s) => (
          <div key={s.id} className="snap-start">
            <StickyCard
              sticky={s}
              onChange={updateSticky}
              onDelete={deleteSticky}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              isDragging={draggingId === s.id}
              isOver={overId === s.id && draggingId !== s.id}
            />
          </div>
        ))}
        <button
          onClick={addSticky}
          disabled={stickies.length >= 50}
          className="flex-shrink-0 w-56 h-40 border-2 border-dashed border-black/40 rounded-md flex flex-col items-center justify-center gap-1 text-black/60 hover:text-black hover:border-black hover:bg-white/40 transition disabled:opacity-40"
          data-testid="sticky-add-btn"
        >
          <Plus className="w-6 h-6" />
          <span className="text-xs font-bold">Add post-it</span>
        </button>
      </div>
    </div>
  );
}
